import React, { useState } from 'react';
import { View } from 'react-native';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramSheaveRPM } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';
import { formatNumber } from '../../../utils/format';
import { useUnits } from '../../../context/UnitsContext';

const KEY = 'tabai.calc.sheaveRpmAndPd.v1';

type Fields = { driverRpm: string; driverPd: string; drivenPd: string; drivenRpm: string };
const EMPTY: Fields = { driverRpm: '', driverPd: '', drivenPd: '', drivenRpm: '' };

/**
 * Sheave RPM & Pulley Diameter — RPM₁ × PD₁ = RPM₂ × PD₂
 */
const SheaveEquationsRPMandPDScreen = () => {
  const [fields, setFields, { clear: clearPersisted }] = usePersistedState<Fields>(KEY, EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [solvedFor, setSolvedFor] = useState<keyof Fields | null>(null);
  const { unit, toImp, fromImp } = useUnits();

  const set = (patch: Partial<Fields>) => setFields((prev) => ({ ...prev, ...patch }));

  const calculate = () => {
    setError(null);
    const filled = (Object.values(fields) as string[]).filter((v) => v !== '').length;
    if (filled < 3) {
      setError('Fill any 3 of the 4 fields to solve for the missing one.');
      return;
    }
    if (filled === 4) {
      setError('All 4 values are filled — clear one to solve for it.');
      return;
    }

    const drpm = parseFloat(fields.driverRpm);
    const dpd = toImp(fields.driverPd, 'diameter');
    const d2pd = toImp(fields.drivenPd, 'diameter');
    const d2rpm = parseFloat(fields.drivenRpm);

    if (fields.drivenRpm === '') {
      if (d2pd === 0) { setError('Driven PD cannot be zero.'); return; }
      setSolvedFor('drivenRpm');
      set({ drivenRpm: formatNumber((drpm * dpd) / d2pd, { decimals: 2 }) });
    } else if (fields.drivenPd === '') {
      if (d2rpm === 0) { setError('Driven RPM cannot be zero.'); return; }
      setSolvedFor('drivenPd');
      set({ drivenPd: fromImp((drpm * dpd) / d2rpm, 'diameter', 2) });
    } else if (fields.driverPd === '') {
      if (drpm === 0) { setError('Driver RPM cannot be zero.'); return; }
      setSolvedFor('driverPd');
      set({ driverPd: fromImp((d2rpm * d2pd) / drpm, 'diameter', 2) });
    } else if (fields.driverRpm === '') {
      if (dpd === 0) { setError('Driver PD cannot be zero.'); return; }
      setSolvedFor('driverRpm');
      set({ driverRpm: formatNumber((d2rpm * d2pd) / dpd, { decimals: 2 }) });
    }
  };

  const clearAll = () => {
    setError(null);
    setSolvedFor(null);
    void clearPersisted();
  };

  const result = [
    fields.driverRpm && { label: 'Driver RPM', value: fields.driverRpm, unit: 'RPM', emphasis: solvedFor === 'driverRpm' },
    fields.driverPd && { label: 'Driver PD', value: fields.driverPd, unit: unit('diameter'), emphasis: solvedFor === 'driverPd' },
    fields.drivenPd && { label: 'Driven PD', value: fields.drivenPd, unit: unit('diameter'), emphasis: solvedFor === 'drivenPd' },
    fields.drivenRpm && { label: 'Driven RPM', value: fields.drivenRpm, unit: 'RPM', emphasis: solvedFor === 'drivenRpm' },
  ].filter(Boolean) as { label: string; value: string; unit?: string; emphasis?: boolean }[];

  return (
    <CalcShell
      title="RPM and Pulley Diameter"
      subtitle="Calculate sheave/pulley relationships for belt-driven equipment"
      formula={
        <>
          <FormulaLine>RPM₁ × PD₁ = RPM₂ × PD₂</FormulaLine>
          <FormulaNote>RPM = Motor speed, PD = Pulley Diameter ({unit('diameter')}). Fill 3 of 4 fields.</FormulaNote>
        </>
      }
      error={error}
      onCalculate={calculate}
      onClear={clearAll}
      result={result.length ? result : null}
      copyFooter="Formula: RPM₁ × PD₁ = RPM₂ × PD₂"
      diagram={<DiagramSheaveRPM />}
      outputSlots={{
        motor_rpm: fields.driverRpm ? parseFloat(fields.driverRpm) : undefined,
        motor_pd_in: fields.driverPd ? toImp(fields.driverPd, 'diameter') : undefined,
        fan_pd_in: fields.drivenPd ? toImp(fields.drivenPd, 'diameter') : undefined,
        fan_rpm: fields.drivenRpm ? parseFloat(fields.drivenRpm) : undefined,
      }}
      onPrefill={(values) => {
        const patch: Partial<Fields> = {};
        if (values.motor_rpm != null && !fields.driverRpm) {
          patch.driverRpm = values.motor_rpm.toFixed(2);
        }
        if (values.motor_pd_in != null && !fields.driverPd) {
          patch.driverPd = fromImp(values.motor_pd_in, 'diameter', 2);
        }
        if (values.fan_pd_in != null && !fields.drivenPd) {
          patch.drivenPd = fromImp(values.fan_pd_in, 'diameter', 2);
        }
        if (values.fan_rpm != null && !fields.drivenRpm) {
          patch.drivenRpm = values.fan_rpm.toFixed(2);
        }
        if (Object.keys(patch).length > 0) set(patch);
      }}
    >
      <View style={{ marginBottom: 12 }}>
        <NumericField label="Driver RPM" value={fields.driverRpm} onChangeText={(v) => set({ driverRpm: v })} rightText="RPM" maxDecimals={2} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label={`Driver PD (${unit('diameter')})`} value={fields.driverPd} onChangeText={(v) => set({ driverPd: v })} rightText={unit('diameter')} maxDecimals={3} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label={`Driven PD (${unit('diameter')})`} value={fields.drivenPd} onChangeText={(v) => set({ drivenPd: v })} rightText={unit('diameter')} maxDecimals={3} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label="Driven RPM" value={fields.drivenRpm} onChangeText={(v) => set({ drivenRpm: v })} rightText="RPM" maxDecimals={2} onSubmitEditing={calculate} />
      </View>
    </CalcShell>
  );
};

export default SheaveEquationsRPMandPDScreen;
